/**
 * Shared appearance integration layer.
 *
 * Bridges the FabricEditor stroke/fill API with the overlay panel.
 * Listens for CustomEvents on `window` and delegates to FabricEditor methods.
 *
 * Events:
 *   shape:set-stroke       { value: "rgba(...)" }                — set stroke color
 *   shape:set-stroke-width { value: number }                     — set stroke width
 *   shape:set-fill         { value: "<css>" }                    — set fill : hex, rgba,
 *                          transparent ou linear-gradient (lib/color_value)
 *   shape:set-shadow-*     (enabled, color, blur, offsetX, offsetY)
 *   shape:set-opacity      { value: 0..100 }                     — l'opacité du calque
 *   shape:set-corner-radius { value: number }                    — l'arrondi (rect, cadre d'image)
 *   text:set-font          { family, weight }                    — set font family/weight
 *   text:set-font-size     { value: number }                     — set font size
 *   text:toggle-style      { style: "bold"|"italic"|"underline" }— toggle a text style
 *   text:set-align         { value: "left"|"center"|"right"|"justify" } — justification
 *
 * En retour, le panneau Police suit ce que le texte porte là où un réglage s'appliquerait
 * (la plage sélectionnée en édition, sinon le texte entier) : `text:style-changed` sur
 * window, à chaque commande, mouvement de sélection, et chargement du panneau — le
 * panneau, rendu serveur depuis le document, ne connaît pas la sélection.
 */
import { isBold, MIXED, type FabricEditor, type TextStyleState } from "@frabr/fabric-editor";
import { cssAngleToLib, parseColorValue } from "./color_value";

/** Ce que le panneau Police reflète : une valeur par contrôle, "mixed" quand la plage
 *  n'est pas homogène (aucun bouton pressé). `font` = "Famille/poids", la clé des boutons. */
export interface TextPanelState {
  font?: string;
  bold?: boolean | typeof MIXED;
  italic?: boolean | typeof MIXED;
  underline?: boolean | typeof MIXED;
  align?: string;
  /** La couleur (hex ou rgba), pour le panneau Remplissage. */
  fill?: string;
}

export const TEXT_STYLE_CHANGED = "text:style-changed";

/** L'ombre posée par « + » (CompositionDocument::Layer::DEFAULT_SHADOW, côté serveur). */
const DEFAULT_SHADOW = { color: "rgba(0,0,0,0.3)", blur: 24, offsetX: 0, offsetY: 8 };

/** Les événements du canvas après lesquels le style au point d'application a pu changer. */
const STYLE_EVENTS = [
  "text:selection:changed", "text:editing:entered", "text:editing:exited",
  "selection:created", "selection:updated", "selection:cleared",
];
const TOOLBOX_FRAME_ID = "toolbox-content";

export function panelState(style: TextStyleState | null, align?: string): TextPanelState {
  if (!style) return {};

  const { fontFamily, fontWeight, fontStyle, underline, fill } = style;
  const mixedFont = fontFamily === MIXED || fontWeight === MIXED;
  return {
    font: mixedFont ? MIXED : `${fontFamily}/${fontWeight}`,
    bold: fontWeight === MIXED ? MIXED : isBold(fontWeight),
    italic: fontStyle === MIXED ? MIXED : fontStyle === "italic",
    underline: underline === MIXED ? MIXED : underline === true,
    align,
    fill: typeof fill === "string" ? fill : undefined,
  };
}

interface FabricAppearanceOpts {
  onChanged?: () => void;
}

interface EventConfig {
  handler: (editor: FabricEditor, detail: Record<string, unknown>) => void;
}

const EVENTS: Record<string, EventConfig> = {
  "shape:set-stroke": {
    handler: (ed, d) => ed.setStrokeColor(String(d.value)),
  },
  "shape:set-stroke-width": {
    handler: (ed, d) => ed.setStrokeWidth(Number(d.value)),
  },
  "shape:set-fill": {
    handler: (ed, d) => {
      const value = parseColorValue(String(d.value));
      if (value.kind === "gradient") {
        ed.setFillGradient(value.gradient.stops, cssAngleToLib(value.gradient.angle));
      } else {
        ed.setFillColor(value.kind === "solid" ? value.color : "transparent");
      }
    },
  },
  "shape:set-opacity": {
    handler: (ed, d) => ed.changeOpacity(Number(d.value)),
  },
  "shape:set-corner-radius": {
    handler: (ed, d) => ed.setCornerRadius(Number(d.value)),
  },
  // Une case (detail.enabled) ou le « + / − » du panneau Apparence (detail.value) ; l'ombre
  // ajoutée est celle de la démo (X 0, Y 8, diffusion 24, noir à 30 %)
  "shape:set-shadow-enabled": {
    handler: (ed, d) => {
      const on = d.enabled ?? d.value === "true";
      if (on) ed.setShadow(DEFAULT_SHADOW);
      else ed.removeShadow();
    },
  },
  "shape:set-shadow-color": {
    handler: (ed, d) => ed.setShadow({ color: String(d.value) }),
  },
  "shape:set-shadow-blur": {
    handler: (ed, d) => ed.setShadow({ blur: Number(d.value) }),
  },
  "shape:set-shadow-offsetX": {
    handler: (ed, d) => ed.setShadow({ offsetX: Number(d.value) }),
  },
  "shape:set-shadow-offsetY": {
    handler: (ed, d) => ed.setShadow({ offsetY: Number(d.value) }),
  },
  "text:set-font": {
    handler: (ed, d) => ed.changeFont(String(d.family), d.weight ? String(d.weight) : undefined),
  },
  "text:set-font-size": {
    handler: (ed, d) => ed.setFontSize(Number(d.value)),
  },
  "text:toggle-style": {
    handler: (ed, d) => ed.toggleTextStyle(d.style as "bold" | "italic" | "underline"),
  },
  "text:set-align": {
    handler: (ed, d) => ed.setTextAlign(d.value as "left" | "center" | "right" | "justify"),
  },
};

// ── Appearance extraction (fabric object → overlay panel params) ────

// ── FabricAppearance ────────────────────────────────────────────────

export class FabricAppearance {
  private editor: FabricEditor;
  private opts: FabricAppearanceOpts;
  private boundHandler: (e: Event) => void;
  private readonly announce = () => this.announceTextStyle();
  private readonly onFrameLoad = (e: Event) => {
    if ((e.target as HTMLElement).id === TOOLBOX_FRAME_ID) this.announceTextStyle();
  };

  constructor(editor: FabricEditor, opts: FabricAppearanceOpts = {}) {
    this.editor = editor;
    this.opts = opts;
    this.boundHandler = (e: Event) => this.handle(e as CustomEvent);
  }

  attach(): void {
    for (const event of Object.keys(EVENTS)) {
      window.addEventListener(event, this.boundHandler);
    }
    for (const event of STYLE_EVENTS) this.editor.canvas.on(event as never, this.announce as never);
    document.addEventListener("turbo:frame-load", this.onFrameLoad);
  }

  destroy(): void {
    for (const event of Object.keys(EVENTS)) {
      window.removeEventListener(event, this.boundHandler);
    }
    for (const event of STYLE_EVENTS) this.editor.canvas.off(event as never, this.announce as never);
    document.removeEventListener("turbo:frame-load", this.onFrameLoad);
  }

  private handle(e: CustomEvent): void {
    const config = EVENTS[e.type];
    if (!config) return;
    const obj = this.editor.selection.current;
    if (!obj) return;
    config.handler(this.editor, e.detail || {});
    this.opts.onChanged?.();
    // Le remplissage aussi : sur un texte, il suit la plage
    if (e.type.startsWith("text:") || e.type === "shape:set-fill") this.announceTextStyle();
  }

  private announceTextStyle(): void {
    const obj = this.editor.selection.current as { textAlign?: string } | null;
    const detail = panelState(this.editor.textStyle(), obj?.textAlign);
    window.dispatchEvent(new CustomEvent(TEXT_STYLE_CHANGED, { detail }));
  }
}
